<template>
  <section v-if="offer && offer.enabled === true" class="syfrus-offer" aria-label="Syfrus free year offer">
    <h2>One year of TradingCandle Pro, free with Syfrus</h2>
    <p v-if="offer && offer.grant">
      Your free year is claimed. Pro access runs through {{ formatDate(offer.grant.expiresAt) }}.
    </p>
    <template v-else>
      <p>Link your Syfrus account, or create one while connecting, then claim a free year. No payment required.</p>
      <p>Already subscribed? Your free year starts after your current paid period. One redemption per TradingCandle and Syfrus account.</p>
    </template>
    <p v-if="offer && offer.redeemedElsewhere">This Syfrus account has already claimed the offer on another TradingCandle account.</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="message" role="status">{{ message }}</p>
    <div class="offer-actions">
      <button v-if="offer && offer.enabled !== false && !offer.linked" :disabled="busy" @click="link">Create or link Syfrus account</button>
      <button v-if="offer && offer.eligible" :disabled="busy" @click="claim">Claim one free year</button>
      <button v-if="error || !offer" :disabled="busy" @click="refresh">Check connection</button>
      <button v-if="error" :disabled="busy" @click="manageAccount">Manage connected accounts</button>
    </div>
    <span v-if="busy" role="status">Checking your account…</span>
  </section>
</template>

<script>
export default {
  name: 'SyfrusOffer',
  data: () => ({ offer: null, busy: false, error: '', message: '' }),
  mounted() { this.refresh(); },
  methods: {
    formatDate(value) { return new Date(value).toLocaleDateString(); },
    async request(path, method = 'GET') {
      await this.$clerk.init();
      const token = await this.$clerk.getToken();
      if (!token) throw new Error('Please sign in again to continue.');
      const response = await fetch(`${process.env.VUE_APP_API_BASE || ''}/api/syfrus/offer${path}`, {
        method, headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Please try again.');
      return data;
    },
    async refresh() {
      this.busy = true;
      this.error = '';
      try { this.offer = await this.request(''); }
      catch (error) { this.error = error.message; }
      finally { this.busy = false; }
    },
    async link() {
      this.busy = true;
      this.error = '';
      try {
        const config = await fetch(`${process.env.VUE_APP_API_BASE || ''}/api/syfrus/config`, { cache: 'no-store' });
        if (!config.ok || !(await config.json()).enabled) throw new Error('Syfrus linking is currently disabled.');
        const clerk = await this.$clerk._ensureLoaded();
        if (!clerk?.user) throw new Error('Please sign in again to continue.');
        await clerk.user.reload();
        const existing = clerk.user.externalAccounts.find(account => ['oauth_custom_syfrus', 'custom_syfrus'].includes(account.provider));
        if (existing?.verification?.status === 'verified') {
          this.offer = await this.request('');
          return;
        }
        const params = { redirectUrl: window.location.origin + '/pricing', oidcPrompt: 'login', additionalScopes: ['openid', 'profile', 'email'] };
        const account = existing ? await existing.reauthorize(params) :
          await clerk.user.createExternalAccount({ ...params, strategy: 'oauth_custom_syfrus' });
        if (account.verification?.status === 'verified') {
          this.offer = await this.request('');
          return;
        }
        const redirect = account.verification?.externalVerificationRedirectURL;
        if (!redirect) throw new Error('Complete Syfrus verification in Manage connected accounts, then check the connection.');
        window.location.assign(redirect.href || String(redirect));
      } catch (error) {
        this.error = error.errors?.[0]?.longMessage || error.message || 'Unable to link Syfrus. Please try again.';
      } finally { this.busy = false; }
    },
    async manageAccount() {
      const clerk = await this.$clerk._ensureLoaded();
      if (clerk) window.location.assign('https://accounts.tradingcandle.com/user');
    },
    async claim() {
      this.busy = true;
      this.error = '';
      try {
        const data = await this.request('/claim', 'POST');
        this.$clerk.setSubscription(data.subscription.plan, data.subscription.expiresAt);
        this.$store.dispatch('activateSubscription', { plan: data.subscription.plan, expiresAt: data.subscription.expiresAt, internationalCharts: data.internationalCharts, indianMarketData: data.indianMarketData });
        this.message = 'Your free year is activated. No payment is due.';
        this.offer = await this.request('');
        this.$emit('claimed');
      } catch (error) { this.error = error.message; }
      finally { this.busy = false; }
    },
  },
};
</script>

<style scoped>
.syfrus-offer { padding: 20px; border: 1px solid #7278c7; border-radius: 12px; margin: 0 0 24px; color: inherit; }
.syfrus-offer h2 { font-size: 20px; margin: 0 0 10px; }
.syfrus-offer p { line-height: 1.5; margin: 8px 0; }
.offer-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; }
.offer-actions button { background: #454bb0; color: white; border: 0; border-radius: 8px; padding: 12px 16px; cursor: pointer; }
.offer-actions button:disabled { opacity: .6; cursor: wait; }
[role="alert"] { color: #c84040; }
</style>
